import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@components/LoadingSpinner';
import { ErrorMessage } from '@components/ErrorMessage';
import { useGlobalVariable } from '@api/enterprise/globalVariable';
import {
  ReviewHeader,
  EquipmentReviewCardList,
  ReviewPartsAndCharges,
  CustomerAcknowledgment,
  JobNotesSection
} from './components';
import { useCustomerReviewData } from './hooks';

export const CustomerReviewPage: React.FC = () => {
  const { jobNumber } = useParams<{ jobNumber: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('customerReview');
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const {
    workOrder,
    equipmentList,
    parts,
    charges,
    isLoading,
    error,
    isOffline,
    isLocked
  } = useCustomerReviewData(jobNumber);

  const { data: pricingVisibilityData } = useGlobalVariable(
    'HIDE_PRICING_CUSTOMER_REVIEW',
    true
  );
  const showPricing = pricingVisibilityData?.text !== 'false';

  const handleBack = () => {
    navigate(`/work/${jobNumber}`);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <LoadingSpinner message={t('loading')} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorMessage
          title={t('error')}
          message={error}
          showRetry
          onRetry={() => window.location.reload()}
        />
      </Container>
    );
  }

  if (!jobNumber || !workOrder) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <ErrorMessage title={t('notFound')} message={t('notFound')} />
      </Container>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: isMobile ? '100vw' : '1600px',
          mx: 'auto',
          px: { xs: 2, sm: 2, md: 4 },
          py: isMobile ? 1.5 : 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <ReviewHeader
          jobNumber={jobNumber}
          onBack={handleBack}
          isOffline={isOffline}
        />

        <EquipmentReviewCardList
          equipmentList={equipmentList}
          jobNumber={jobNumber}
        />

        <ReviewPartsAndCharges
          parts={parts}
          charges={charges}
          jobNumber={jobNumber}
          hidePricing={!showPricing}
        />

        <JobNotesSection
          jobNumber={jobNumber}
          initialNotes={workOrder.notes || ''}
          disabled={isLocked}
        />

        <CustomerAcknowledgment
          jobNumber={jobNumber}
          equipmentList={equipmentList}
          isLocked={isLocked}
        />
      </Container>
    </Box>
  );
};
